import { Box, Button, Flex, HStack, Icon, Textarea } from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { MealPlanSummaryItem } from '@web-app/server/api/types';
import { capitalizeWord } from '@web-app/utils/format.utils';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue } from 'jotai';
import set from 'lodash/set';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconType } from 'react-icons';
import { PiBowlFood, PiCoffee, PiCookie, PiHamburger } from 'react-icons/pi';
import ResizeTextarea from 'react-textarea-autosize';

import { BerryLoader } from '../BerryLoader';
import { Text } from '../Typography/Text';
import { MealPlanStepHeader } from './MealPlanStepHeader';
import { RegenerateButton } from './RegenerateButton';
import { MealPlanView, MEALS, MealType } from './utils';

const MEAL_ICONS: Record<MealType, IconType> = {
  breakfast: PiCoffee,
  lunch: PiHamburger,
  dinner: PiBowlFood,
  snack: PiCookie,
};

export interface MealPlanSummaryProps {
  readOnly: boolean;
}

export const MealPlanSummary = ({ readOnly }: MealPlanSummaryProps) => {
  const {
    mealPlanSummaryAtom,
    isMealPlanSummaryLoadingAtom,
    analyticsContextAtom,
  } = useMolecule(mealPlanMolecule);
  const [mealPlanSummary, setMealPlanSummary] = useAtom(mealPlanSummaryAtom);
  const isPending = useAtomValue(isMealPlanSummaryLoadingAtom);
  const analyticsContext = useAtomValue(analyticsContextAtom);
  const { trackEvent } = useAnalytics();
  useEffect(
    function trackOutlineViewEvent() {
      trackEvent(EVENT_NAMES.VIEW_MEAL_PLAN_OUTLINE, {
        context: analyticsContext,
      });
    },
    [trackEvent, analyticsContext],
  );

  if (isPending) {
    return (
      <Flex
        width="100%"
        minHeight="300px"
        flex={1}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <BerryLoader />
        <Text>Loading meal plan</Text>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap={4} mb={8}>
      <Flex justifyContent="space-between" alignItems="flex-end">
        <MealPlanStepHeader step={MealPlanView.Summary} />
        {!readOnly && <RegenerateButton step={MealPlanView.Summary} />}
      </Flex>
      {mealPlanSummary && (
        <MealPlanSummaryEditBlock
          mealPlanSummary={mealPlanSummary}
          setMealPlanSummary={setMealPlanSummary}
          readOnly={readOnly}
        />
      )}
    </Flex>
  );
};

export const MealPlanSummaryEditBlock = ({
  mealPlanSummary,
  setMealPlanSummary,
  readOnly,
}: {
  mealPlanSummary: MealPlanSummaryItem;
  setMealPlanSummary: (data: MealPlanSummaryItem | null) => void;
  readOnly: boolean;
}) => {
  const { trackEvent } = useAnalytics();
  const { analyticsContextAtom } = useMolecule(mealPlanMolecule);
  const analyticsContext = useAtomValue(analyticsContextAtom);
  const editAlreadyRun = useRef(false);
  const [activeDay, setActiveDay] = useState(0);
  const trackOutlineChange = useCallback(() => {
    if (!editAlreadyRun.current) {
      editAlreadyRun.current = true;
      trackEvent(EVENT_NAMES.EDITS_MEAL_PLAN_OUTLINE, {
        context: analyticsContext,
      });
    }
  }, [trackEvent, analyticsContext]);

  if (!mealPlanSummary.mealPlan) return null;

  const dayCount = mealPlanSummary.mealPlan.length;
  const currentDay = mealPlanSummary.mealPlan[activeDay];

  if (!currentDay) return null;

  return (
    <Flex flexDir="column" gap={4}>
      <Box mt={4}>
        <HStack spacing={1} overflowX="auto" role="tablist">
          {Array.from({ length: dayCount }, (_, i) => (
            <Button
              key={`day-tab-${i}`}
              role="tab"
              aria-selected={i === activeDay}
              size="sm"
              variant={i === activeDay ? 'solid' : 'ghost'}
              onClick={() => setActiveDay(i)}
              minW="auto"
              px={4}
              fontWeight={i === activeDay ? 600 : 400}
            >
              Day {i + 1}
            </Button>
          ))}
        </HStack>
        <Box borderBottomWidth="1px" borderColor="border.default" mb={9} />
      </Box>

      <Flex flexDir="column" gap={5}>
        {MEALS.map((mealType) => (
          <Box key={`day-${activeDay}-${mealType}`}>
            <HStack spacing={2} mb={2}>
              <Icon as={MEAL_ICONS[mealType]} boxSize={5} color="text.muted" />
              <Text textType="sm" fontWeight={600}>
                {capitalizeWord(mealType)}
              </Text>
            </HStack>
            {readOnly ? (
              <Text textType="sm" color="text.default">
                {currentDay[mealType]}
              </Text>
            ) : (
              <Textarea
                as={ResizeTextarea}
                bg="bg.default"
                py={1.5}
                px={3}
                value={currentDay[mealType]}
                onChange={(e) => {
                  trackOutlineChange();
                  const newMealPlan = { ...mealPlanSummary };
                  set(
                    newMealPlan,
                    `mealPlan[${activeDay}].${mealType}`,
                    e.target.value,
                  );
                  setMealPlanSummary(newMealPlan);
                }}
                borderColor="border.default"
                minRows={1}
                rows={1}
                maxRows={6}
              />
            )}
          </Box>
        ))}
      </Flex>
    </Flex>
  );
};
